import {
  checkNetwork,
  getBalanceObject,
  getDelegations,
  loadWeb3,
} from "@flare.space/ftso-web3";
import {
  switchToCoston2Chain,
  switchToCostonChain,
  switchToFlareChain,
  switchToSongbirdChain,
} from "@flare.space/ftso-web3/dist/network";
import CookieService from "./cookie.service";
import ToastService from "./toast.service";
import { AccountType, Chain, Store } from "./store.service";
import { ethers } from "ethers";
import { BalanceObject } from "../models/BalanceObject";

interface FtsoService {
  ACCOUNT_IS_CONNECTED: boolean;
  ACCOUNT_TYPE: string;
  ACCOUNT: string;
  listeners: any;
  initialized: boolean;

  init: (force?: boolean) => Promise<string>;
  listen: (name: string, listener: () => void) => void;
  notifyListeners: () => void;
  setAccount: (account: string, type: AccountType) => void;
  setChain: (chain: Chain) => void;
  changeNetwork: () => Promise<boolean>;
  getBalance: (
    address: string,
    currency?: "FLR" | "SGB"
  ) => Promise<BalanceObject | null>;
  resetAccountData: (manageCookies: boolean) => void;
  getDelegations: (address: string) => Promise<any>;
}

const FtsoService: FtsoService = {
  ACCOUNT_IS_CONNECTED: false,
  ACCOUNT_TYPE: "",
  ACCOUNT: "",
  listeners: {},
  initialized: false,

  init: async (force?: boolean) => {
    console.log("Initializing FtsoService");
    if (FtsoService.initialized && !force) {
      return "terminated";
    }
    let chain: Chain | null = null;
    const wallet = Store.accountType.get().toString();

    const prevMode = CookieService.getCookie(CookieService.CHAIN_KEY) as Chain;
    if (!prevMode) {
      chain = Chain.FLR;
      CookieService.setCookie(CookieService.CHAIN_KEY, Chain.FLR);
    } else {
      chain = prevMode;
    }

    if (chain != null) {
      FtsoService.setChain(chain);

      let success = true;
      if (Store.accountType.get() === AccountType.MetaMask) {
        const success = await FtsoService.changeNetwork();
      }
      try {
        // Try to load metamask. If it fails it probably means the user doesn't have the metamask extension.
        // At this point we don't show any errors, since they might use WalletConnect instead.
        await loadWeb3();
      } catch (e) {
        console.warn(e);
      }
      // Get the account data stored in storage (e.g. if the user was logged in on the landing page)
      const type = CookieService.getCookie(CookieService.COOKIE_ACCOUNT_TYPE);
      const account = CookieService.getCookie(CookieService.COOKIE_ACCOUNT);
      if (account && type) {
        FtsoService.setAccount(account, type as AccountType);
      }
      FtsoService.initialized = true;

      return success ? "confirmed" : "rejected:" + chain;
    } else {
      FtsoService.initialized = true;
      return "rejected:" + chain;
    }
  },

  listen: (name: string, listener: () => void) => {
    FtsoService.listeners[name] = listener;
  },

  notifyListeners: () => {
    Object.keys(FtsoService.listeners).forEach((name: string) => {
      FtsoService.listeners[name]();
    });
  },
  resetAccountData(manageCookies: boolean) {
    if (manageCookies) {
      CookieService.setCookie(CookieService.COOKIE_ACCOUNT, null);
      CookieService.setCookie(CookieService.COOKIE_ACCOUNT_TYPE, null);
    }
  },

  setAccount: (account: string, type: AccountType) => {
    FtsoService.ACCOUNT = account;
    FtsoService.ACCOUNT_TYPE = type;
    FtsoService.ACCOUNT_IS_CONNECTED = account.length ? true : false;
    FtsoService.notifyListeners();

    if (account.length) {
      Store.account.set(account);
      Store.accountType.set(type);
      Store.accountConnected.set(true);

      CookieService.setCookie(CookieService.COOKIE_ACCOUNT, account);
      CookieService.setCookie(CookieService.COOKIE_ACCOUNT_TYPE, type);
      if (type !== AccountType.Simulator) {
        FtsoService.resetAccountData(false);
      }
      if (window.ethereum && !checkNetwork("Flare")) {
        switchToFlareChain().then((_) => {
          console.log("Switched to Flare chain");
        });
      }

      const showConnectedToast = Store.showConnectedToast.get();
      if (showConnectedToast) {
        console.log("showConnectedToast2", Store.showConnectedToast.get());
        ToastService.success(
          "Successful connection",
          "Your wallet was connected successfully."
        );
        Store.showConnectedToast.set(false);
      }
    } else {
      console.log("Resetting account data");
      FtsoService.resetAccountData(true);
      Store.account.set("");
      Store.accountType.set(AccountType.NONE);
      Store.accountConnected.set(false);
      Store.showConnectedToast.set(true);
    }
  },

  setChain: (chain: Chain) => {
    Store.chain.set(chain);
    CookieService.setCookie(CookieService.CHAIN_KEY, chain);
  },

  changeNetwork: async () => {
    switch (Store.chain.get()) {
      case Chain.FLR:
        if (window.ethereum && !checkNetwork("Flare")) {
          const success = await switchToFlareChain();
          console.log("Switched to Flare chain");
          return success;
        }
        return true;
      case Chain.SGB:
        if (window.ethereum && !checkNetwork("Songbird")) {
          const success = switchToSongbirdChain();
          console.log("Switched to Songbird chain");
          return success;
        }
        return true;
      case Chain.COSTON:
        if (window.ethereum && !checkNetwork("Coston")) {
          const success = switchToCostonChain();
          console.log("Switched to Coston chain");
          return success;
        }
        return true;
      case Chain.COSTON2:
        if (window.ethereum && !checkNetwork("Coston2")) {
          const success = switchToCoston2Chain();
          console.log("Switched to Coston2 chain");
          return success;
        }
        return true;
    }
  },
  getBalance: async (address: string, currency: "FLR" | "SGB" = "FLR") => {
    if (!address) {
      return null;
    }

    try {
      const trimmed = ethers.utils.getAddress(address.trim());
      const balance = await getBalanceObject(trimmed, currency);
      Store.balance.set(balance);
      return balance;
    } catch (e: any) {
      ToastService.error("RPC Error", e.message, {
        toastId: "toast-rpc-error",
      });
      console.error(e);
      return null;
    }
  },

  getDelegations: async (address: string) => {
    try {
      const chain = "FLR"; // Store.chain.get()  => hardcoded chain for this app
      const balance = Store.balance.get();

      const delegations = await getDelegations(address, chain);
      console.log("delegations ftso service", delegations);
      if (
        delegations._delegateAddresses === null ||
        delegations._delegateAddresses.length === 0
      ) {
        return [];
      }

      const delegators = [];

      for (const index in delegations[0]) {
        const percent = Number(delegations[1][index]) / 100.0;
        const delegatorAddress = delegations[0][index];
        //const hubAddress = FtsoService.getFTSO_ADDRESS();
        console.log(
          "delegation value",
          (percent * balance.balanceWrapped) / 100
        );
        const delegator = {
          value: (percent * balance.balanceWrapped) / 100,
          address: delegatorAddress as string,
          percent: percent,
        };

        delegators.push(delegator);
      }
      return delegators;

      // TODO add to store
      //Store.delegations.set(delegators);
    } catch (e: any) {
      ToastService.error("RPC Error", e.message, {
        toastId: "toast-rpc-error",
      });
    }
  },
};

export default FtsoService;
